import { useState } from "react"
import { Check, Info, X } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import {
  barSentence,
  contactMarker,
  dayPercent,
  todayInfo,
  visitMarker,
  ZONE_RULER,
  zones,
  zoneSpan,
  type ClockAction,
  type MarkerSymbol,
  type MarkerTone,
} from "../lib/clock"
import type { TcmEpisode } from "../types"
import { ClockTip } from "./clock/ClockTip"

/**
 * The 30-day TCM period as one bar (Darshak's clock, 2026-10-23):
 * - three zones: days 0-7 (99496 or 99495), 8-14 (99495 only), 15-29 (not TCM-billable), in neutral
 *   shades, named by the ruler in the column header (ClockRuler) and by their tooltips;
 * - the 2-day contact deadline as a flag above the bar, the visit as a marker on it, and today as a line;
 * - every part is a button with a tooltip (hover, focus or tap), and each state has its own symbol so
 *   the clock reads without color.
 * `onAction` (case panel only) puts one action button in the deadline and visit tooltips.
 */
export function TcmClock({
  episode,
  today,
  ehr,
  size = "sm",
  onAction,
}: {
  episode: TcmEpisode
  today: string
  ehr?: string
  size?: "sm" | "lg"
  onAction?: (mode: ClockAction) => void
}) {
  if (!episode.clock) return null
  const lg = size === "lg"
  const contact = contactMarker(episode, today)
  const visit = visitMarker(episode, today, ehr)
  const now = todayInfo(episode)
  return (
    <div
      role="group"
      aria-label={barSentence(episode, today)}
      className={cn("relative w-full", lg ? "pt-7 pb-1" : "pt-5")}
    >
      <div className={cn("relative w-full", lg ? "h-3" : "h-2.5")}>
        {/* Zones: neutral shades; their meaning is in the ruler and the tooltips, not in a color. */}
        <div className="absolute inset-0 overflow-hidden rounded-full bg-muted">
          <div className="absolute inset-y-0 bg-foreground/25" style={{ left: 0, width: `${zoneSpan(ZONE_RULER[0]).width}%` }} />
          <div className="absolute inset-y-0 bg-foreground/[0.12] border-l border-card" style={{ left: `${zoneSpan(ZONE_RULER[1]).left}%`, width: `${zoneSpan(ZONE_RULER[1]).width}%` }} />
          <div className="absolute inset-y-0 border-l border-card" style={{ left: `${zoneSpan(ZONE_RULER[2]).left}%` }} />
        </div>
        {/* Today: a plain line; it carries no status color. */}
        <div
          className={cn("pointer-events-none absolute -ml-px w-0.5 rounded-full bg-foreground", lg ? "-top-2 h-7" : "-top-1.5 h-[22px]")}
          style={{ left: `${dayPercent(now.day)}%` }}
          aria-hidden
        />
      </div>

      {/* Order in the DOM = keyboard order: deadline, visit, today, then the zones. */}
      {contact && (
        <ClockTip
          info={contact}
          onAction={onAction}
          className={cn("z-20 h-8 w-8 -ml-4", lg ? "top-0" : "-top-1.5")}
          style={{ left: `${dayPercent(contact.day)}%` }}
        >
          <span className="flex flex-col items-center">
            <Glyph tone={contact.tone} symbol={contact.symbol} small={!lg} />
            <span className={cn("w-px bg-foreground/40", lg ? "h-2" : "h-1")} aria-hidden />
          </span>
        </ClockTip>
      )}
      {visit && (
        <ClockTip
          info={visit}
          onAction={onAction}
          className={cn("z-30 h-8 w-8 -ml-4", lg ? "top-3" : "top-1")}
          style={{ left: `${dayPercent(visit.day)}%` }}
        >
          <Glyph tone={visit.tone} symbol={visit.symbol} small={!lg} />
        </ClockTip>
      )}
      <ClockTip
        info={now}
        className={cn("z-10 h-8 w-4 -ml-2 rounded-sm", lg ? "top-3" : "top-1")}
        style={{ left: `${dayPercent(now.day)}%` }}
      >
        <span className="sr-only">Today</span>
      </ClockTip>
      {zones(episode).map((z) => {
        const span = zoneSpan(z)
        return (
          <ClockTip
            key={z.id}
            info={z}
            className={cn("z-0 h-8 rounded-sm", lg ? "top-3" : "top-1")}
            style={{ left: `${span.left}%`, width: `${span.width}%` }}
          >
            <span className="sr-only">{z.title}</span>
          </ClockTip>
        )
      })}
    </div>
  )
}

const TONE: Record<MarkerTone, string> = {
  red: "bg-destructive border-destructive text-destructive-foreground",
  amber: "bg-warning border-warning text-warning-foreground",
  green: "bg-success border-success text-success-foreground",
  outline: "bg-card border-primary text-primary",
  gray: "bg-card border-muted-foreground/60 text-muted-foreground",
}

/** A marker drawn with its tone and its symbol: ✕ act now, ! today, ✓ done, a ring when booked. */
export function Glyph({ tone, symbol, small }: { tone: MarkerTone; symbol: MarkerSymbol; small?: boolean }) {
  const icon = small ? "h-2.5 w-2.5" : "h-3 w-3"
  return (
    <span
      className={cn(
        "flex items-center justify-center rounded-full border-2 font-bold leading-none shadow-sm",
        small ? "h-4 w-4 text-[10px]" : "h-5 w-5 text-[11px]",
        TONE[tone],
      )}
      aria-hidden
    >
      {symbol === "x" && <X className={icon} strokeWidth={3.5} />}
      {symbol === "check" && <Check className={icon} strokeWidth={3.5} />}
      {symbol === "bang" && "!"}
      {symbol === "dot" && <span className="h-1 w-1 rounded-full bg-current" />}
    </span>
  )
}

/** The zone ruler that sits in the column header, aligned with every bar below it. */
export function ClockRuler({ className }: { className?: string }) {
  return (
    <div className={cn("relative h-8 w-full", className)} aria-hidden>
      {ZONE_RULER.map((z) => {
        const span = zoneSpan(z)
        const [days, codes] = z.ruler.split(" · ")
        return (
          <div
            key={z.id}
            className="absolute top-0 border-l border-border pl-1 leading-tight first:border-l-0 first:pl-0"
            style={{ left: `${span.left}%`, width: `${span.width}%` }}
            title={z.ruler}
          >
            <div className="text-[10px] font-semibold normal-case tracking-normal text-foreground/80">{days}</div>
            <div className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">{codes}</div>
          </div>
        )
      })}
    </div>
  )
}

const KEY_ROWS: { glyph: React.ReactNode; text: string }[] = [
  { glyph: <span className="h-2.5 w-6 rounded-sm bg-foreground/25" />, text: "Days 0–7: a visit can bill 99496 or 99495" },
  { glyph: <span className="h-2.5 w-6 rounded-sm bg-foreground/[0.12]" />, text: "Days 8–14: 99495 only" },
  { glyph: <span className="h-2.5 w-6 rounded-sm bg-muted ring-1 ring-inset ring-border" />, text: "Days 15–29: not TCM-billable" },
  { glyph: <Glyph tone="gray" symbol="dot" />, text: "2-day contact due (the flag above the bar)" },
  { glyph: <Glyph tone="red" symbol="x" />, text: "Contact missed, or visit date passed: act now" },
  { glyph: <Glyph tone="amber" symbol="bang" />, text: "Due today, visit today, or contact late" },
  { glyph: <Glyph tone="green" symbol="check" />, text: "Done: contact made (or 2 attempts), patient seen" },
  { glyph: <Glyph tone="outline" symbol="ring" />, text: "Visit booked" },
  { glyph: <span className="h-4 w-0.5 rounded-full bg-foreground" />, text: "Today" },
]

/** ⓘ beside the column title: the clock's key, every marker at full size. */
export function ClockKey() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="How to read the clock"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3 motion-reduce:animate-none">
        <p className="text-[13px] font-semibold">How to read the clock</p>
        <p className="mt-0.5 text-xs text-muted-foreground">Point at, tap or tab to any marker for its date and next step.</p>
        <ul className="mt-2.5 space-y-2">
          {KEY_ROWS.map((r) => (
            <li key={r.text} className="flex items-center gap-2.5 text-xs normal-case tracking-normal font-normal text-foreground">
              <span className="flex w-7 shrink-0 justify-center">{r.glyph}</span>
              {r.text}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

const HINT_KEY = "tcm-clock-hint-dismissed"

/** "Hover any marker for details", shown once per browser, dismissed for good. */
export function ClockHint() {
  const [show, setShow] = useState(() => {
    try {
      return window.localStorage.getItem(HINT_KEY) !== "1"
    } catch {
      return false
    }
  })
  if (!show) return null
  return (
    <div className="flex items-center gap-2 rounded-lg border border-info/30 bg-info/10 px-3 py-1.5 text-xs text-info">
      <Info className="h-3.5 w-3.5 shrink-0" />
      <span>New: point at, tap or tab to any part of the 30-day clock for its date and next step.</span>
      <button
        type="button"
        className="ml-auto font-medium underline-offset-2 hover:underline"
        onClick={() => {
          try {
            window.localStorage.setItem(HINT_KEY, "1")
          } catch {
            /* private window: the hint just shows again next time */
          }
          setShow(false)
        }}
      >
        Got it
      </button>
    </div>
  )
}
